export type Role = "admin" | "warehouse_supervisor" | "picker";
export interface OneCBackgroundStatus { owner: string; heartbeatAt: string; requestedAt: string | null; scheduledAt: string | null; completedAt: string | null; pendingKeys: number; batches: number; lastBatchSeconds: number; error: string }
export interface OneCLocalStatus { background?: OneCBackgroundStatus | null }
export interface OneCLocalStatus { transport?: "LocalCom" | "Http" | "ReverseHttps"; deltaEnabled?: boolean; fullSyncIntervalSeconds?: number; lastPollStartedAt?: string; lastPollDurationSeconds?: number; lastFullScheduledAt?: string; lastFullCompletedAt?: string; pendingSourceChanges?: number; pendingFullChecks?: number; enabled: boolean; operational: boolean; intervalSeconds: number; mirror?: OneCOperationalRun | null; database: string; sourceDatabaseId: string; lastSyncAt: string | null; error: string; imported: number; held: number; pendingEvents: number; pendingInbound?: number; exchangeMetrics?: { pendingOperations: number; acknowledgedOperations: number; pendingIssues: number }; failNextIssueAcknowledgement?: boolean; eventDeliveryPaused?: boolean; injectedFailureEventId?: string | null }
export type OrderStatus = "New" | "Assigned" | "Picking" | "Picked" | "Issued" | "PartiallyPicked" | "Shortage" | "Cancelled";
export type PickingTaskStatus = "Assigned" | "InProgress" | "Completed" | "Cancelled";
export interface ReviewReturn { taskLineId: string; locationBarcode: string; productBarcode: string; quantity: number }
export interface OrderReviewPlan {
  messageId: string; revision: number; action: "rebuild" | "cancel"; reason: string; stagingCode: string; stagingBarcode: string;
  incomingLines: Array<{ key: number; productCode: string; productName: string; quantity: number }>;
  returns: Array<{ taskLineId: string; productCode: string; productName: string; locationCode: string; locationBarcode: string; quantity: number }>;
}
export interface Session {
  token: string;
  expiresAt: string;
  name: string;
  email: string;
  roles: Role[];
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  isActive: boolean;
}

export interface Warehouse {
  id: string;
  externalId: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface Location {
  isStaging: boolean;
  id: string;
  code: string;
  barcode: string;
  zone?: string;
  aisle?: string;
  rack?: string;
  shelf?: string;
  isActive: boolean;
}

export interface Balance {
  warehouseId: string;
  locationId: string;
  locationCode: string;
  locationBarcode: string;
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  unit: string;
}

export interface OneCPayment {
  receivableDebt?: number | null; advanceBalance?: number | null; receivableError?: string; shipmentCount?: number;
  returnDetails?: { summary: string; handover: string; timing: string; goods: string; documents: Array<{kind: string; id: string; type: string; number: string; date: string; amount: number; warehouse: string}> };

  orderAmount: number; returnedAmount: number; receivedAmount: number; refundedAmount: number;
  paidAmount: number; remaining: number; overpayment: number; currency: string;
  status: string; error: string; capturedAt: string;
}

export interface OrderSummary {
  payment?: OneCPayment | null;
  id: string;
  externalId: string;
  number: string;
  orderedAt: string;
  status: OrderStatus;
  warehouse: string;
  customer: string;
  linesCount: number;
  needsReview?: boolean;
}

export interface OrderDetail {
  payment?: OneCPayment | null;
  customerReturns?: Array<{ id: string; number: string; state: string; historical: boolean; received: number; placed: number }>;
  canIssue: boolean;
  canVerifyRelease: boolean;
  fulfillmentVersion: number;
  releaseDecision: "Blocked" | "Paid" | "CreditApproved";
  releaseReference: string;
  events?: Array<{ id: string; eventType: string; sourceOrderRevision?: number; exchangeState: string; actorId?: string; actorName?: string }>;
  releaseValidUntil: string | null;
  oneCWorkflow?: { needsReview: boolean; reviewReason?: string; currencyId: string; documentAmount: number; orderRevision: number; paidAmount?: number; authorizedBy?: string } | null;
  id: string;
  externalId: string;
  number: string;
  orderedAt: string;
  issuedAt?: string | null;
  status: OrderStatus;
  warehouse: { id: string; code: string; name: string };
  customer: { code: string; name: string };
  lines: Array<{
    id: string;
    externalLineId: string;
    productCode: string;
    productName: string;
    requestedQuantity: number;
    pickedQuantity: number;
    issuedQuantity: number;
    unit: string;
  }>;
}

export interface PartnerFulfillmentSummary {
  id: string;
  number: string;
  orderedAt: string;
  status: OrderStatus;
  customer: string;
  warehousesCount: number;
  readyWarehousesCount: number;
  linesCount: number;
  shortageCount: number;
}

export interface PartnerFulfillmentDetail {
  id: string;
  number: string;
  orderedAt: string;
  deliveryAddress: string;
  status: OrderStatus;
  customer: { code: string; name: string };
  shortages: Array<{ code: string; name: string; unit: string; requestedQuantity: number; allocatedQuantity: number; missingQuantity: number }>;
  warehouses: Array<{
    orderId: string;
    status: OrderStatus;
    warehouse: { id: string; code: string; name: string };
    lines: Array<{ id: string; productCode: string; productName: string; unit: string; requestedQuantity: number; pickedQuantity: number }>;
  }>;
}

export interface PickingTask {
  id: string;
  status: PickingTaskStatus;
  orderStatus: OrderStatus;
  releaseDecision: "Blocked" | "Paid" | "CreditApproved";
  stagingLocationCode?: string | null;
  orderId: string;
  orderNumber: string;
  customer: string;
  lines: Array<{
    id: string;
    locationCode: string;
    locationBarcode: string;
    productCode: string;
    productName: string;
    barcodes: string[];
    requiredQuantity: number;
    pickedQuantity: number;
    unit: string;
  }>;
}

export interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  traceId?: string;
}

export interface EdoSyncResult {
  method: string;
  received: number;
  added: number;
  updated: number;
  deferred: number;
}

export interface EdoTradeDocument {
  id: string; number: string; method: string; documentDate: string; warehouseName: string;
  currencyId: string; course: number | null; sourceStatus: string; validationIssue: string;
  stockState: string; financialState: string;
  lines: {sourceRow: number; productName: string; quantity: number; sourcePrice: number | null;
    sourceAmount: number | null; costAmountKgs: number | null; amountKgs: number | null}[];
}

export interface EdoStockReceipt {
  id: string;
  number: string;
  documentDate: string;
  warehouseName: string;
  warehouseId: string;
  warehouseExternalId: string;
  stockEffect: "AwaitingPlacement" | "Placed";
  stockNote: string;
  lines: Array<{
    id: string; sourceRow: number; productCode: string; productName: string; unit: string;
    quantity: number; sourcePrice: number; sourceAmount: number; costAmountKgs: number;
    placedQuantity: number; remainingQuantity: number;
    externalQuantity: number | null; externalCapturedAt: string | null; cellQuantity: number | null;
  }>;
}

export interface EdoStatus {
  configured: boolean;
  automaticSyncEnabled: boolean;
  syncIntervalMinutes: number;
  orderSyncEnabled: boolean;
  messageSyncEnabled: boolean;
  messageReviewOnly: boolean;
  outboundDocumentSyncEnabled: boolean;
  databaseIdConfigured: boolean;
  pendingOutbox: number;
  failedOutbox: number;
  latestOutbox: Array<{
    id: string;
    eventType: string;
    aggregateId: string;
    attemptCount: number;
    lastAttemptAt?: string;
    nextAttemptAt?: string;
    lastError: string;
    acknowledgedAt?: string;
    createdAt: string;
  }>;
  inbox: Array<{ status: string; count: number }>;
  latestRuns: Array<{
    method: string;
    succeeded: boolean;
    receivedCount: number;
    addedCount: number;
    updatedCount: number;
    error: string;
    createdAt: string;
    completedAt?: string;
  }>;
}

export interface EdoOutboxDispatchResult {
  sent: number;
  failed: number;
  skipped: number;
}

export const unitLabels: Record<string, string> = {
  Piece: "шт",
  Kilogram: "кг",
  SquareMeter: "м²",
  LinearMeter: "пог. м",
  Meter: "м",
  Liter: "л",
  Package: "упак.",
  Pair: "пар",
  Kit: "компл.",
  Roll: "рул.",
  Set: "набор"
};

export const statusLabels: Record<OrderStatus, string> = {
  New: "Новый",
  Assigned: "Назначен",
  Picking: "В сборке",
  Picked: "Собран",
  Issued: "Выдан",
  PartiallyPicked: "Собран частично",
  Shortage: "Недостача",
  Cancelled: "Отменён"
};

export interface AbcXyzReportItem {
  productId: string;
  productCode: string;
  productName: string;
  totalSoldQuantity: number;
  totalRevenue: number;
  totalCost: number | null;
  averagePurchasePrice: number | null;
  averageSalePrice: number | null;
  unit: string;
  customerCount: number;
  totalMargin: number | null;
  marginPercent: number | null;
  currentAvailableQuantity: number;
  abcClass: string;
  xyzClass: string;
}

export interface AbcXyzReportResponse {
  items: AbcXyzReportItem[];
  xyzAvailable: boolean;
  hasUndatedHistoricalData: boolean;
  historicalPeriodStart: string | null;
  historicalPeriodEnd: string | null;
  note: string | null;
}

export interface ProductCatalogItem {
  id: string;
  code: string;
  name: string;
  unit: string;
  purchasePrice: number;
  salePrice: number;
  totalQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  isStorefrontPublished: boolean;
  categoryId: string | null;
  category: string;
  brand: string;
  description: string;
  specifications: Record<string, string>;
  images: Array<{ id: string; fileName: string; contentType: string; altText: string; sortOrder: number }>;
}

export interface ProductCategoryItem {
  id: string;
  name: string;
  isActive: boolean;
  hasImage: boolean;
  productCount: number;
  publishedProductCount: number;
  products: Array<{
    id: string;
    code: string;
    name: string;
    salePrice: number;
    isStorefrontPublished: boolean;
  }>;
}

export interface MoveInventoryRequest {
  operationId: string;
  sourceLocationBarcode: string;
  targetLocationBarcode: string;
  productCode: string;
  quantity: number;
}

export interface CustomerAnalyticsItem {
  customerId: string;
  customerCode: string;
  customerName: string;
  totalRevenue: number;
  totalMargin: number | null;
  marginPercent: number | null;
  orderCount: number | null;
  productCount: number;
  revenueShare: number;
}

export interface CustomerAnalyticsResponse {
  items: CustomerAnalyticsItem[];
  hasUndatedHistoricalData: boolean;
  historicalPeriodStart: string | null;
  historicalPeriodEnd: string | null;
  note: string | null;
}

export interface CustomerProductAnalyticsItem {
  productId: string;
  productName: string;
  unit: string;
  quantity: number;
  revenue: number;
  revenueShare: number;
  averagePurchasePrice: number | null;
  averageSalePrice: number | null;
  margin: number | null;
  marginPercent: number | null;
  currentAvailableQuantity: number;
}

export interface CustomerProductsAnalyticsResponse {
  items: CustomerProductAnalyticsItem[];
}

export interface InventoryAssetItem {
  productId: string;
  productCode: string;
  productName: string;
  unit: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  purchasePrice: number;
  salePrice: number;
  costValue: number;
  saleValue: number;
  potentialMargin: number;
  periodSoldQuantity: number;
  periodRevenue: number;
  periodDays: number;
  averageDailySales: number;
  stockCoverageDays: number | null;
  turnoverCategory: string;
}

export interface InventoryAssetsResponse {
  items: InventoryAssetItem[];
  totalCostValue: number;
  totalSaleValue: number;
  totalPotentialMargin: number;
  productsCount: number;
  soldProductsCount: number;
  periodRevenue: number;
  periodStart: string | null;
  periodEnd: string | null;
  periodDays: number;
}

export interface ExpenseAnalyticsItem {
  id: string;
  category: string;
  article: string;
  analytic: string;
  amount: number;
  sharePercent: number;
  sourceRow: number;
}

export interface ExpenseGroupTotal {
  name: string;
  amount: number;
  sharePercent: number;
  itemsCount: number;
}

export interface ExpenseArticleTotal {
  category: string;
  article: string;
  amount: number;
  sharePercent: number;
  analyticsCount: number;
}

export interface ExpenseAnalyticsResponse {
  costOfSalesAmount: number;
  otherExpensesAmount: number;
  items: ExpenseAnalyticsItem[];
  categoryTotals: ExpenseGroupTotal[];
  articleTotals: ExpenseArticleTotal[];
  totalAmount: number;
  categoriesCount: number;
  articlesCount: number;
  periodStart: string | null;
  periodEnd: string | null;
  note: string | null;
}

export interface ProfitAnalyticsResponse {
  revenue: number;
  costOfSales: number | null;
  grossProfit: number | null;
  otherExpenses: number;
  netProfit: number | null;
  netMarginPercent: number | null;
  expenseCostOfSalesExcluded: number;
  periodStart: string | null;
  periodEnd: string | null;
  ready: boolean;
  hasMovements: boolean;
  message: string;
  salesSyncedAt: string | null;
  expensesSyncedAt: string | null;
}

export interface MarginAlertItem {
  orderNumber: string;
  orderedAt: string;
  customerName: string;
  productCode: string;
  productName: string;
  quantity: number;
  salePrice: number;
  purchasePrice: number;
  margin: number;
}

export interface MarginAlertsResponse {
  items: MarginAlertItem[];
  historicalDataExcluded: boolean;
  note: string | null;
}

export interface OneCOperationalRun { products: number; warehouses: number; customers: number; stockRows: number; initializedBalances: number; negativeStockRows: number; variantStockRows: number; unsupportedProducts: number; activeOrders: number; heldOrders: number; archivedOrders: number; cycles: number; capturedAt: string; issuesJson: string }
export interface OneCSourceOrder { id: string; number: string; orderedAt: string; customerName: string; sourceState: string; amount: number; disposition: string; reason: string; bodyJson: string }

export interface OneCStockComparison { warehouse: string; productCode: string; productName: string; unit: string; sourceQuantity: number; physicalQuantity: number; reservedQuantity: number; capturedAt: string }
